import { memo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import Money from '#client/components/ListTransaction/components/Money';
import DirectionTypeIcon from '#client/components/TransactionDirectionTypeIcon';
import { DATE_FORMAT } from '#client/constants/date';
import { MONEY_COLOR } from '#client/constants/transaction';
import useModalContext from '#client/hooks/useModalContext';

import { StackStyled } from './AuthorizationTransactionRow.styles';
import AuthorizationDetailModal from './components/AuthorizationDetailModal';

function AuthorizationTransactionRow({
  id,
  createdAt,
  summary,
  direction,
  amount,
  type,
  status,
  reason,
  cardId,
  paymentMethod,
  digitalWallet,
  cardPresent,
  recurring,
  verificationMethod,
  merchantName,
  merchantLocation,
  merchantCategory,
  currencyConversion
}) {
  const { openModal } = useModalContext();

  const trackButtonPressed = useButtonPressedCallback({
    elementName: 'link_account_modal',
    elementText: 'transactions_expanded',
    appPageName: 'active_banking_dashboard'
  });

  const clickHandler = () => {
    // Tracking Details: Tracking added to click on transactions to see details
    trackButtonPressed();

    openModal({
      component: AuthorizationDetailModal,
      amount,
      createdAt,
      type,
      status,
      summary,
      id,
      reason,
      cardId,
      // Merchant Details
      paymentMethod,
      digitalWallet,
      cardPresent,
      recurring,
      verificationMethod,
      merchantName,
      merchantLocation,
      merchantCategory,
      // FX Details
      currencyConversion
    });
  };

  return (
    <Stack
      onClick={clickHandler}
      role="listitem"
      sx={{
        cursor: 'pointer',
        padding: '15px 10px',
        '&:hover': {
          boxShadow: '0px 4px 16px 2px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <StackStyled
        pr={0}
        width="100%"
      >
        <StackStyled width="85%">
          <Stack
            alignItems="center"
            direction="row"
            spacing={{ xs: '16px', md: '32px' }}
          >
            <Stack>
              <DirectionTypeIcon direction={direction} />
            </Stack>
            <Stack
              alignItems="start"
              direction="column"
              spacing={1}
            >
              <Text
                size="md"
                weight="medium"
              >
                {summary}
              </Text>
              <Text
                color="tertiary"
                size="xs"
                whiteSpace="nowrap"
              >
                {dayjs(createdAt).format(DATE_FORMAT.PRIMARY)}
              </Text>
            </Stack>
          </Stack>
        </StackStyled>
        <Stack
          alignItems="end"
          direction="column"
        >
          <Money
            amount={amount}
            color={MONEY_COLOR.TERTIARY}
            direction={direction}
          />
        </Stack>
      </StackStyled>
    </Stack>
  );
}

AuthorizationTransactionRow.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  direction: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  cardId: PropTypes.string,
  reason: PropTypes.string,
  // Merchant Detail
  paymentMethod: PropTypes.string,
  digitalWallet: PropTypes.string,
  cardPresent: PropTypes.bool,
  recurring: PropTypes.bool,
  verificationMethod: PropTypes.string,
  merchantName: PropTypes.string,
  merchantLocation: PropTypes.string,
  merchantCategory: PropTypes.string,
  // FX Details
  currencyConversion: PropTypes.shape({
    originalCurrency: PropTypes.string,
    amountInOriginalCurrency: PropTypes.number,
    fxRate: PropTypes.string
  })
};

export default memo(AuthorizationTransactionRow);
